import React, { useState, useRef, useEffect, useMemo, useCallback, useLayoutEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import { TextToolBar } from '../Layout/TextToolBar';
import { useResourceContext } from '../../contexts/ResourceContext';
import { useChatContext } from '../../contexts/AppDataContext';
import { useWindow } from '../../contexts/WindowContext';
import VersionControls from './VersionControls';
import { CodeDiffButton } from './CodeDiffButton';
import { ResourcesActionButton } from './Buttons/ResourcesActionButton';
import { ResourcesListOverlay } from './ResourcesListOverlay';
import { CloseButton } from '../Layout/Buttons/CloseButton';
import StudyPanelTitle from './StudyPanelTitle';
import PreviewToggleButton from './Buttons/PreviewToggleButton';
import ContentDisplayView from './ContentDisplayView';
import { isPreviewable } from './sandboxUtils';
import { PlusSignIcon } from 'hugeicons-react';
import { useAttachmentContext } from '../../contexts/AttachmentContext';
import { ResourceDraftEditor } from './ResourceDraftEditor';
import { Resource } from '../../types/resource';
import '../../Styles/Layout/Resources/ContentViewer.css';

interface ExpandedResourceProps {
  onSelectionAction: (quotedText: string, message: string) => void;
}

const ExpandedResource: React.FC<ExpandedResourceProps> = ({  
  onSelectionAction,
}) => {
  const { 
    expandedResource: resource, 
    expandedVersion: desiredVersion, 
    currentResourceVersions, 
    toggleResourceVisibility, 
    isResourcesListVisible, 
    isReadOnlyResources,
  } = useResourceContext();
  const { currentChatId: chatId } = useChatContext();
  const {
    textFiles,
    activeTextAttachmentFileId,
    isTextAttachmentViewOpen,
    handleCreateDocumentClick,
  } = useAttachmentContext();
  const [selectedText, setSelectedText] = useState('');
  const [showOptions, setShowOptions] = useState(false);
  const [optionsPosition, setOptionsPosition] = useState({ top: 0, left: 0 });
  const contentRef = useRef<HTMLDivElement>(null);
  const selectedRangeRef = useRef<Range | null>(null);
  const toolbarFrameRef = useRef<number | null>(null);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [showDiff, setShowDiff] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const { openNewWindowModal } = useWindow();

  const versions = useMemo(() => {
    if (currentResourceVersions && currentResourceVersions.length > 0) {
      return [...currentResourceVersions].sort((a, b) => (a.version ?? 1) - (b.version ?? 1));
    }
    return resource ? [resource] : [];
  }, [currentResourceVersions, resource]);

  const activeAttachment = useMemo(
    () => isTextAttachmentViewOpen
      ? textFiles.find(file => file.id === activeTextAttachmentFileId) || null
      : null,
    [activeTextAttachmentFileId, isTextAttachmentViewOpen, textFiles]
  );
  const isActiveDraft = Boolean(
    activeAttachment?.isLoaded &&
    activeAttachment.id.startsWith('create-') &&
    !activeAttachment.url
  );

  const attachmentResource = useMemo<Resource | null>(() => {
    if (!activeAttachment) return null;
    return {
      id: activeAttachment.id,
      type: activeAttachment.type,
      title: activeAttachment.name,
      content: activeAttachment.content || '',
      timestamp: new Date().toISOString(),
      chatId: chatId || 'pending',
      version: 1,
      language: activeAttachment.codeLanguage,
      url: activeAttachment.url,
      isTextAttachment: true,
    };
  }, [activeAttachment, chatId]);

  useEffect(() => {
    function handleIframeMessage(event: MessageEvent) {
      if (typeof event.data === 'object' && event.data && event.data.type === 'external-link') {
        const url = event.data.url;
        if (typeof url === 'string') {
          openNewWindowModal(url);
        }
      }
    }
    window.addEventListener('message', handleIframeMessage);
    return () => {
      window.removeEventListener('message', handleIframeMessage);
    };
  }, [openNewWindowModal]);

  const contentVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
  };

  const currentResourceVersion = versions.length > currentIndex ? versions[currentIndex] : undefined;
  const displayedResource = attachmentResource || currentResourceVersion;
  const isCode = displayedResource?.type === 'code';
  const isQuiz = displayedResource?.type === 'quiz';
  const isPdf = displayedResource?.type === 'pdf';

  useEffect(() => {
    if (!resource || versions.length === 0) {
      setCurrentIndex(0);
      return;
    }

    const lastVersionIndex = versions.length - 1;

    if (desiredVersion) {
      setCurrentIndex(Math.max(0, Math.min(desiredVersion - 1, lastVersionIndex)));
    } else if (resource.messageId) {
      const versionIndex = versions.findIndex(v => v.messageId === resource.messageId);
      setCurrentIndex(versionIndex !== -1 ? versionIndex : lastVersionIndex);
    } else {
      setCurrentIndex(lastVersionIndex);
    }
  }, [resource, desiredVersion, versions]);

  // Default previewable (HTML / React) code resources to preview view on
  // first observation of each (resource id, version). Locked once initialized
  // so subsequent re-renders (e.g. streaming content updates within the same
  // version) don't fight the user's toggle. Switching versions re-applies.
  const previewInitKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (activeAttachment) {
      previewInitKeyRef.current = null;
      setShowPreview(false);
      return;
    }
    if (!currentResourceVersion || currentResourceVersion.type !== 'code') {
      previewInitKeyRef.current = null;
      return;
    }
    const key = `${currentResourceVersion.id}::${currentResourceVersion.version ?? 0}`;
    if (previewInitKeyRef.current === key) return;
    if (isPreviewable(currentResourceVersion.content || '', currentResourceVersion.language)) {
      previewInitKeyRef.current = key;
      setShowPreview(true);
    }
  }, [activeAttachment, currentResourceVersion]);

  // Toolbar is positioned from selection rects; scrolling leaves it visually wrong — hide it.
  useEffect(() => {
    if (!showOptions) return;
    const el = contentRef.current;
    if (!el) return;

    const hideToolbar = () => {
      setShowOptions(false);
    };

    const wheelOpts: AddEventListenerOptions = { capture: true, passive: true };
    el.addEventListener('scroll', hideToolbar, { passive: true });
    // Nested overflow areas don't bubble `scroll` to this node; capture `wheel` inside content.
    el.addEventListener('wheel', hideToolbar, wheelOpts);

    return () => {
      el.removeEventListener('scroll', hideToolbar);
      el.removeEventListener('wheel', hideToolbar, wheelOpts);
    };
  }, [showOptions]);

  const placeSelectionToolbar = useCallback(() => {
    if (toolbarFrameRef.current !== null) {
      window.cancelAnimationFrame(toolbarFrameRef.current);
    }

    toolbarFrameRef.current = window.requestAnimationFrame(() => {
      const range = selectedRangeRef.current;
      const content = contentRef.current;
      const panel = content?.parentElement;
      if (!range || !content || !panel) return;

      const selectionBounds = range.getBoundingClientRect();
      const lineRects = Array.from(range.getClientRects());
      const lastLine = lineRects[lineRects.length - 1];
      if (!lastLine || selectionBounds.width === 0) return;

      const panelBounds = panel.getBoundingClientRect();
      setOptionsPosition({
        top: Math.round(lastLine.bottom - panelBounds.top + 8),
        left: Math.round(selectionBounds.left - panelBounds.left + selectionBounds.width / 2),
      });
      toolbarFrameRef.current = null;
    });
  }, []);

  useLayoutEffect(() => {
    if (showOptions) placeSelectionToolbar();
  }, [showOptions, placeSelectionToolbar]);

  useEffect(() => {
    if (!showOptions) return;
    const panel = contentRef.current?.parentElement;
    if (!panel) return;

    const observer = new ResizeObserver(placeSelectionToolbar);
    observer.observe(panel);
    window.addEventListener('resize', placeSelectionToolbar);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', placeSelectionToolbar);
      if (toolbarFrameRef.current !== null) {
        window.cancelAnimationFrame(toolbarFrameRef.current);
        toolbarFrameRef.current = null;
      }
    };
  }, [showOptions, placeSelectionToolbar]);

  const handleMouseUp = () => {
    // Selection actions send a message, which a shared chat can't take.
    if (activeAttachment || isReadOnlyResources) return;
    const selection = window.getSelection();
    if (!selection || isResourcesListVisible) return;
    
    const text = selection.toString().trim();
    
    const isWithinContent = contentRef.current?.contains(selection.anchorNode);
    
    if (!isWithinContent) {
      selectedRangeRef.current = null;
      setShowOptions(false);
      return;
    }

    const isRemovedDiffLine = selection.anchorNode?.parentElement?.closest('.code-diff-removed');
    const supportsSelectionActions = displayedResource
      && ['doc', 'essay', 'code', 'quiz'].includes(displayedResource.type);

    if (
      text &&
      supportsSelectionActions &&
      !isRemovedDiffLine
    ) {
      setSelectedText(text);
      
      selectedRangeRef.current = selection.getRangeAt(0).cloneRange();
      placeSelectionToolbar();
      setShowOptions(true);
    } else {
      selectedRangeRef.current = null;
      setShowOptions(false);
    }
  };

  const handleSelectionAction = (
    e: React.SyntheticEvent,
    action: 'explain' | 'improve' | 'shorten' | 'tone' | 'grammar' | 'custom',
    customPrompt?: string,
  ) => {
    e.stopPropagation();

    const subject = isCode ? 'code selection' : 'selected passage';
    const instructions = {
      explain: `Explain this ${subject} in more detail.`,
      improve: `Improve this ${subject} for clarity, precision, and readability.`,
      shorten: `Shorten this ${subject} while preserving its essential meaning.`,
      tone: `Rewrite this ${subject} in a clear, polished, and approachable tone.`,
      grammar: `Correct the grammar and wording in this ${subject} without changing its meaning.`,
      custom: isCode
        ? `Edit this code selection according to this instruction: ${customPrompt || ''}`
        : `Edit this selected passage according to this instruction: ${customPrompt || ''}`,
    };

    onSelectionAction(selectedText, instructions[action]);
    selectedRangeRef.current = null;
    setShowOptions(false);
  };


  const renderContent = () => {
    if (activeAttachment) {
      const isEditable =
        isActiveDraft &&
        (activeAttachment.type === 'doc' || activeAttachment.type === 'code') &&
        activeAttachment.content !== null;
      if (isEditable) {
        return <ResourceDraftEditor file={activeAttachment} onDone={toggleResourceVisibility} />;
      }
      if (!attachmentResource) return null;
      return (
        <ContentDisplayView
          currentResourceVersion={attachmentResource}
          chatId={chatId || ''}
          showDiff={false}
          versions={[attachmentResource]}
          currentIndex={0}
          showPreview={false}
        />
      );
    }
    if (!currentResourceVersion) return null;

    return (
      <ContentDisplayView
        currentResourceVersion={currentResourceVersion}
        chatId={chatId || ''}
        showDiff={showDiff}
        versions={versions}
        currentIndex={currentIndex}
        showPreview={showPreview}
        readOnly={isReadOnlyResources}
      />
    );
  };
  
  return (
    <div className="expanded-resource-container">
      <div className="resource-panel-header flex items-center justify-between px-4 py-2">
        <div className="resource-panel-header-main flex items-center gap-3 min-w-0 flex-1">
          <ResourcesListOverlay />
          <StudyPanelTitle />
        </div>
        <div className="flex items-center gap-2">
          {!isReadOnlyResources && (
            <button
              type="button"
              className="resource-create-button"
              onClick={() => handleCreateDocumentClick('doc')}
              aria-label="Create document"
              title="Create document"
            >
              <PlusSignIcon size={16} />
            </button>
          )}
          {!activeAttachment && currentResourceVersion && (
            <ResourcesActionButton
              resource={currentResourceVersion}
              onOpenFile={currentResourceVersion.url ? () => openNewWindowModal(currentResourceVersion.url!) : undefined}
            />
          )}
          <CloseButton
            onClick={toggleResourceVisibility}
          />
        </div>
      </div> 
      <div 
        onMouseUp={handleMouseUp}
        ref={contentRef}
        className={`
          expanded-resource-content
          ${activeAttachment || isCode || isQuiz || isPdf  ? 'noPadding' : ''}
        `}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={activeAttachment
              ? `attachment-${activeAttachment.id}`
              : currentResourceVersion
                ? `${currentResourceVersion.id}-${currentResourceVersion.version}-${showDiff}`
                : 'no-resource'}
            variants={contentVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            style={{ height: '100%' }}
          >
            {activeAttachment || currentResourceVersion ? renderContent() : null}
          </motion.div>
        </AnimatePresence>
      </div>

      {showOptions && (
        <TextToolBar
          optionsPosition={optionsPosition}
          contentContainerRef={contentRef}
          handleMouseUp={handleMouseUp}
          onAction={handleSelectionAction}
        />
      )}

      {!activeAttachment && !isQuiz && (
        <div className="resource-footer">
          <div className='resource-footer-left'>
            <VersionControls
              currentIndex={currentIndex}
              versions={versions}
              setCurrentIndex={setCurrentIndex}
            />
            {isCode && currentIndex > 0 && (
              <CodeDiffButton showDiff={showDiff} setShowDiff={setShowDiff} />
            )}
            {isCode && (
              <PreviewToggleButton
                showPreview={showPreview}
                setShowPreview={setShowPreview}
                content={currentResourceVersion?.content || ''}
                language={currentResourceVersion?.language}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpandedResource;
